import PageRendererHelperBase from './PageRendererHelperBase';
import { fromJS } from 'immutable';
import { fabric } from 'fabric';
import PicabooConstants from '../../../../common/PicabooConstants';
import FabricUtils from '../../../../utils/FabricUtils';
import { DecimalToHex } from '../../../../utils/color';

export default class PageRendererRichTextHelper extends PageRendererHelperBase {
  drawCaption(
    pi,
    wellWidth,
    wellHeight,
    x,
    y,
    envelope = false,
    newStyle = undefined,
    productInfo = undefined,
  ) {
    return new Promise((resolve) => {
      let addEvents = false;
      let caption = this.getExistingCanvasObject(pi);
      let existingPI = caption ? caption.pageItemVO : null;

      const usePlaceHolderText =
        this._interactive &&
        (pi.get('text') == undefined || pi.get('text').length == 0);
      const captionText = usePlaceHolderText
        ? PicabooConstants.DEFAULT_RT_CAPTION
        : pi.get('text');

      if (captionText === null) {
        resolve(false);
      }

      let styles = pi.get('styles').toJS();
      styles = FabricUtils.adjustFontSizes(styles, true, productInfo);

      //we occasionally get styles that are null, if any styles are null, removed them here.
      let lineKeys = Object.keys(styles);
      lineKeys.forEach((lineKey) => {
        if (!styles[lineKey]) {
          delete styles[lineKey];
        } else {
          let charKeys = Object.keys(styles[lineKey]);
          charKeys.forEach((charKey) => {
            if (!styles[lineKey][charKey]) {
              delete styles[lineKey][charKey];
            } else {
              let styleSettingKeys = Object.keys(styles[lineKey][charKey]);
              styleSettingKeys.forEach((styleSetting) => {
                if (styles[lineKey][charKey][styleSetting] == null) {
                  delete styles[lineKey][charKey][styleSetting];
                }
              });
            }
          });
        }
      });

      const fontSize = FabricUtils.productFontScaled(
        pi.get('fontSize'),
        productInfo,
      );

      let fontFamily =
        this.isStylesValid(styles) && styles[0][0].fontFamily != undefined
          ? styles[0][0].fontFamily
          : pi.get('fontFamily')
            ? pi.get('fontFamily')
            : 'arial';

      let fontWeight =
        this.isStylesValid(styles) && styles[0][0].fontWeight != undefined
          ? styles[0][0].fontWeight
          : pi.get('fontWeight')
            ? pi.get('fontWeight')
            : 'normal';
      let fontStyle =
        this.isStylesValid(styles) && styles[0][0].fontStyle != undefined
          ? styles[0][0].fontStyle
          : pi.get('fontStyle')
            ? pi.get('fontStyle')
            : 'normal';
      let underline =
        this.isStylesValid(styles) && styles[0][0].underline != undefined
          ? styles[0][0].underline
          : pi.get('underline')
            ? pi.get('underline')
            : false;
      let height = wellHeight / this._dim.scale;
      let captionProps = {
        ...this.getDefaultItemOptions(),
        width: wellWidth / this._dim.scale,
        height: height,
        maxHeight: pi.get('maxHeight')
          ? (pi.get('maxHeight') * this._dim.ratioWidth) / this._dim.scale
          : height,
        left: x,
        top: y,
        angle: pi.get('rotation'),
        fontFamily: fontFamily.includes(' ')
          ? `"${fontFamily}", NotDef`
          : `${fontFamily}, NotDef`,
        fontSize: fontSize,
        maxFontSize: fontSize, //Scale according to current font size
        fontWeight: fontWeight,
        fontStyle: fontStyle,
        backgroundColor: pi.get('showBackground')
          ? pi.get('backgroundColor')
          : null,
        textAlign: pi.get('textAlign') ? pi.get('textAlign') : 'left',
        fill:
          pi.has('foilColor') && !global.isRenderer
            ? pi.get('foilColor')
            : this.isStylesValid(styles) && styles[0][0].fill != undefined
              ? styles[0][0].fill
              : pi.get('fill'),
        hasRotatingPoint: true,
        hasBorders: true,
        pageItemVO: pi,
        scaleX: this._dim.scale,
        scaleY: this._dim.scale,
        styles: styles,
        underline: underline,
      };

      this.mobileControls(captionProps);

      if (
        !global.isDesigner &&
        (pi.get('type') === PicabooConstants.WELL_TYPES.ALWAYS_ON_TOP ||
          pi.get('type') === PicabooConstants.WELL_TYPES.FIXED_ELEMENT)
      ) {
        captionProps.hasControls = false;
        captionProps.lockMovementX = true;
        captionProps.lockMovementY = true;
        captionProps.selectable = true;
        captionProps.editable = true;
        captionProps.hoverCursor = 'default';
      } else {
        captionProps.editable = true;
      }

      if (envelope) {
        captionProps.hasControls = false;
        captionProps.lockMovementX = true;
        captionProps.lockMovementY = true;
      }

      if (caption) {
        caption.set(captionProps);
        caption.set('text', captionText);
      } else {
        caption = new fabric.Textbox(captionText, captionProps);
        addEvents = true;
      }

      this.populateShadow(pi, null, caption, true, false);

      //stroke doesn't have to accommodate for scale
      if (pi.has('stroke') && pi.get('stroke') !== false) {
        if (
          !existingPI ||
          (existingPI.get('stroke') &&
            !existingPI.get('stroke').equals(pi.get('stroke')))
        ) {
          let stroke = pi.get('stroke').toJS();
          if (stroke.enabled) {
            //******** NEW TEXT STYLES WHEN AVAILABLE
            caption.strokeWidth = stroke.width;
            caption.stroke = stroke.color;
          } else {
            caption.strokeWidth = 0;
            caption.stroke = null;
          }
        }
      }
      if (pi.has('charSpacing') && pi.get('charSpacing') !== false) {
        let charSpaceAmount =
          (pi.get('charSpacing') * this.ratioWidth) / this._dim.scale;
        caption.charSpacing = charSpaceAmount;
      }

      if (pi.has('lineHeight') && pi.get('lineHeight') !== false) {
        caption.lineHeight = pi.get('lineHeight');
      }

      if (pi.has('scaleX') && pi.get('scaleX') !== false) {
        caption.scaleX = caption.scaleX * pi.get('scaleX');
      }

      if (pi.has('scaleY') && pi.get('scaleY') !== false) {
        caption.scaleY = caption.scaleY * pi.get('scaleY');
      }

      resolve({
        textbox: caption,
        sendChange: false,
        height: wellHeight,
        addEvents,
      });
    });
  }

  isStylesValid(styles) {
    return (
      styles &&
      styles.length > 0 &&
      styles[0] !== undefined &&
      styles[0] !== null &&
      styles[0].length > 0 &&
      styles[0][0] !== undefined &&
      styles[0][0] !== null
    );
  }

  applyStyleToCaption(pi, newStyle, position) {
    let styles = pi.get('styles').toJS();
    let caption = this.getExistingCanvasObject(pi);

    if (!caption) {
      return null;
    }

    let captionProps = {
      backgroundColor: pi.get('showBackground')
        ? pi.get('backgroundColor')
        : null,
      textAlign: pi.get('textAlign') ? pi.get('textAlign') : 'left',
      fill: pi.has('foilColor')
        ? pi.get('foilColor')
        : styles.length > 0 && styles[0][0] != undefined
          ? styles[0][0].fill
          : pi.get('fill'),
    };

    const selectionLength = caption.getSelectedText().length;
    if (
      newStyle &&
      (selectionLength === 0 || selectionLength === pi.get('text').length)
    ) {
      // Set a style for the entire caption by setting an attribute on the page item itself

      let captionPI = caption.pageItemVO;
      Object.keys(newStyle).forEach((key) => {
        captionPI = captionPI.set(key, newStyle[key]);

        // We're going to set a style property directly on page item VO, so first we need to
        // clear it from any selection styles.
        // Note that removing styles like this may leave behind empty objects in the styles object,
        // but they will be removed later in FabricUtils.deleteEmptyStyleObjects()
        const originalStart = caption.selectionStart;
        caption.selectAll();
        caption.setSelectionStyles({ [key]: undefined });
        caption.setSelectionStart(originalStart);
        caption.setSelectionEnd(originalStart);

        // NOTE: we don't scale the font size value for the page item VO, but we need to scale it for the fabric object
        if (key === 'fontSize') {
          const fontSize = FabricUtils.productFontScaled(
            newStyle[key],
            this._productInfo,
          );
          captionProps[key] = fontSize;
        } else if (key === 'fontFamily') {
          captionProps[key] = newStyle[key].includes(' ')
            ? `"${newStyle[key]}", NotDef`
            : `${newStyle[key]}, NotDef`;
        } else {
          captionProps[key] = newStyle[key];
        }
      });
      caption.pageItemVO = captionPI;
    }

    caption.set(captionProps);
    if (selectionLength && newStyle) {
      if (newStyle.hasOwnProperty('fontSize')) {
        newStyle.fontSize = FabricUtils.productFontScaled(
          newStyle.fontSize,
          this._productInfo,
        );
      }
      if (position) {
        caption.setSelectionStyles(newStyle, position - 1, position);
      } else {
        caption.setSelectionStyles(newStyle);
      }
    }
    caption.setCoords();

    return caption;
  }

  replaceCaptionText(pi, start, end, newText) {
    let caption = this.getExistingCanvasObject(pi);
    if (start > -1) {
      let styles = caption.getSelectionStyles(start, start + 1, true);
      let style = styles[0];
      let newStyles = [];
      for (let i = 0; i < newText.length; i++) {
        newStyles.push(style);
      }
      caption.text = newText;

      caption.insertChars(newText, newStyles, start, end);
      this._canvas.requestRenderAll();
    }
    return caption;
  }

  replaceText(pi, newText, isDefaultCaption) {
    let caption = this.getExistingCanvasObject(pi);
    const minFontsize = FabricUtils.productFontScaled(
      PicabooConstants.MIN_FONT_SIZE,
      this._productInfo,
    ); // Divide by fontsize multiplier for correct comparison

    if (pi.has('ignoredWords')) {
      caption.set({
        text: newText,
        charLimitExceeded: false,
        ignoredWords: pi.get('ignoredWords'),
      });
    } else {
      caption.set({ text: newText, charLimitExceeded: false });
    }

    this._canvas.requestRenderAll();
    return caption;
  }
}
